import { useCallback, useEffect, useState } from 'react';
import type { PortalConnectionResponse } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';

// Every mounted hook re-reads when a mutation elsewhere in the tab (the settings
// page's disconnect) changes a connection, so the app-wide banner drops without
// a reload. A renewal leaves the SPA for IS24 and comes back to a fresh mount.
const listeners = new Set<() => void>();

export function notifyPortalConnectionChanged(): void {
  listeners.forEach((listener) => listener());
}

// Every portal's connection in one read (IMM-246.1): the settings page and the app-wide banner.
// A save or a disconnect elsewhere refreshes every mounted copy through `listeners`.
export function usePortalConnections(workspaceId: string | undefined) {
  const [connections, setConnections] = useState<ReadonlyArray<PortalConnectionResponse> | null>(null);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(async () => {
    if (workspaceId === undefined) return;

    setLoadError(false);

    try {
      const result = await apiClient.portalConnections.list({ pathParams: { workspaceId } });

      if (result.ok) setConnections(result.body.connections);
      else setLoadError(true);
    } catch {
      setLoadError(true);
    }
  }, [workspaceId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (workspaceId === undefined) return undefined;

    listeners.add(load);

    return () => {
      listeners.delete(load);
    };
  }, [workspaceId, load]);

  return { connections, loadError, reload: load };
}
